"use client"

// Client-side platform cards for /download.
//
// Highlights the visitor's platform with a "Recommended for your device"
// badge. OS detection reuses the PlatformProvider already mounted in
// app/layout.tsx (same parseOsFromUserAgent chain as the rest of the site);
// Apple Silicon vs Intel is refined via UA-Client-Hints high-entropy values
// where available (Chromium), normalised by the shared normalizeArch helper.
// Safari/Firefox don't expose UA-CH — for those the Mac recommendation
// defaults to Apple Silicon (every Mac sold since 2020), and the
// "About This Mac" hint under the grid covers the Intel case.
//
// Download links are plain <a> tags — these are external GitHub release
// URLs, so client-side routing buys nothing — and each click fires a
// `download_click` event to both GA (gtag) and the Datadog browser logger
// (same pattern as components/NewsletterPopup.tsx), tagged with the
// platform key.

import { useEffect, useState } from "react"
import Image from "next/image"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Check, Download } from "lucide-react"
import { cn } from "@/lib/utils"
import { usePlatform } from "@/lib/contexts/PlatformContext"
import { normalizeArch } from "@/lib/device-arch"
import { useLogger } from "@/lib/hooks/use-logger"
import {
  APP_VERSION,
  DOWNLOAD_MAC_ARM64,
  DOWNLOAD_MAC_INTEL,
  DOWNLOAD_WINDOWS,
} from "@/lib/downloads"

type PlatformKey = "mac-arm64" | "mac-intel" | "windows"

interface PlatformCardData {
  platform: PlatformKey
  label: string
  subtitle: string
  href: string
  iconSrc: string
  iconAlt: string
  size: string
  note: string
}

const PLATFORM_CARDS: PlatformCardData[] = [
  {
    platform: "mac-arm64",
    label: "macOS",
    subtitle: "Apple Silicon (M1 / M2 / M3 / M4)",
    href: DOWNLOAD_MAC_ARM64,
    iconSrc: "/apple.svg",
    iconAlt: "Apple",
    size: "~150 MB",
    note: ".dmg",
  },
  {
    platform: "mac-intel",
    label: "macOS",
    subtitle: "Intel (x64)",
    href: DOWNLOAD_MAC_INTEL,
    iconSrc: "/apple.svg",
    iconAlt: "Apple",
    size: "~160 MB",
    note: ".dmg",
  },
  {
    platform: "windows",
    label: "Windows",
    subtitle: "10 / 11 · 64-bit",
    href: DOWNLOAD_WINDOWS,
    iconSrc: "/windows.svg",
    iconAlt: "Windows",
    size: "~140 MB",
    note: ".exe installer",
  },
]

// Minimal UA-Client-Hints surface — navigator.userAgentData isn't in the
// standard TS DOM lib yet. Only the fields normalizeArch consumes.
interface NavigatorUAData {
  getHighEntropyValues?: (hints: string[]) => Promise<{
    architecture?: string
    bitness?: string
  }>
}

export default function DownloadCards() {
  const os = usePlatform()
  const logger = useLogger()
  const [recommended, setRecommended] = useState<PlatformKey | null>(null)

  useEffect(() => {
    if (os === "windows") {
      setRecommended("windows")
      return
    }
    if (os !== "mac") {
      setRecommended(null)
      return
    }

    let cancelled = false
    const uaData = (navigator as Navigator & { userAgentData?: NavigatorUAData }).userAgentData

    if (uaData?.getHighEntropyValues) {
      uaData
        .getHighEntropyValues(["architecture", "bitness"])
        .then(({ architecture, bitness }) => {
          if (cancelled) return
          const arch = normalizeArch(architecture, bitness)
          setRecommended(arch === "x64" || arch === "x86" ? "mac-intel" : "mac-arm64")
        })
        .catch(() => {
          if (!cancelled) setRecommended("mac-arm64")
        })
    } else {
      setRecommended("mac-arm64")
    }

    return () => {
      cancelled = true
    }
  }, [os])

  const handleDownloadClick = (platform: PlatformKey) => {
    window.gtag?.("event", "download_click", { platform })
    logger.info("download_click", {
      event: "download_click",
      platform,
    })
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
      {PLATFORM_CARDS.map((card) => {
        const isRecommended = recommended === card.platform
        return (
          <Card
            key={card.platform}
            className={cn(
              "bg-white/80 dark:bg-zinc-900/60 border-purple-200/50 dark:border-white/10 backdrop-blur-sm hover:shadow-xl transition-shadow",
              isRecommended &&
                "border-purple-400 dark:border-purple-500/60 ring-1 ring-purple-400/50 dark:ring-purple-500/40"
            )}
          >
            <CardHeader className="flex flex-row items-center gap-3 pb-2">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-100 to-blue-100 dark:from-purple-900/40 dark:to-blue-900/40 flex items-center justify-center">
                <Image
                  src={card.iconSrc}
                  alt={card.iconAlt}
                  width={22}
                  height={22}
                  className="w-5 h-5"
                />
              </div>
              <div>
                <CardTitle className="text-lg text-gray-900 dark:text-white">
                  {card.label}
                </CardTitle>
                <p className="text-xs text-gray-500 dark:text-gray-400">{card.subtitle}</p>
              </div>
            </CardHeader>
            <CardContent className="pt-2 space-y-3">
              {isRecommended && (
                <span className="inline-flex items-center gap-1.5 rounded-full bg-green-100 dark:bg-green-900/40 px-2.5 py-1 text-[11px] font-semibold text-green-700 dark:text-green-300">
                  <Check className="w-3 h-3" />
                  Recommended for your device
                </span>
              )}
              <Button
                asChild
                className="w-full bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white border-0"
              >
                <a
                  href={card.href}
                  rel="noopener"
                  onClick={() => handleDownloadClick(card.platform)}
                  className="flex items-center justify-center gap-2"
                >
                  <Download className="w-4 h-4" />
                  Download v{APP_VERSION}
                </a>
              </Button>
              <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
                <span>{card.size}</span>
                <span>{card.note}</span>
              </div>
            </CardContent>
          </Card>
        )
      })}
    </div>
  )
}
